import { api, auth, type HuntMediaInput } from './api';
import { readGpsTrackPoints, safeLocalStorageGet, safeLocalStorageSet } from './karta/gpsUtils';
import {
  HUNT_MEDIA_MARGIN_MS, HuntMediaNative, classifyLibraryItems, findLibraryMedia, isAutoEligible, isHuntMediaAvailable,
  parseMarkArea, toHuntMediaInput, uploadLibraryItem, type LibraryAccess, type MatchedItem,
} from './huntMedia';

/**
 * A3: "Hämta jaktbilder automatiskt". Med inställningen på laddas bilder och klipp som togs under en
 * jakt, på eller nära marken, upp till jakten (och därmed markens album) utan att jägaren väljer —
 * komprimerade och bara på wifi om inte mobildata uttryckligen tillåts. Av som standard; frågan
 * ställs första gången det finns bilder från en jakt (HuntMediaSection).
 *
 * Jaktfönstren sparas på telefonen när jakten startar (App.tsx) och när en avslutad jakt öppnas, så
 * att bilder som kommer in senare (t.ex. Ray-Ban-klipp som Meta AI-appen sparar i efterhand, eller
 * som väntat på wifi) hämtas nästa gång appen öppnas. Allt som laddats upp minns per bild, så inget
 * laddas upp två gånger, även om sparandet i API:t misslyckades efter uppladdningen.
 */

// ── Inställning (per person på den här telefonen) ───────────────────────────

export interface HuntMediaPrefs {
  /** null = inte tillfrågad än */
  auto: 'on' | 'off' | null;
  /** Ladda upp även via mobildata (annars väntar det på wifi). */
  allowCellular: boolean;
}

const prefsKey = () => `qvenari_hunt_media_prefs_v1:${auth.getUser()?.sub || 'anon'}`;

export function readHuntMediaPrefs(): HuntMediaPrefs {
  try {
    const p = JSON.parse(safeLocalStorageGet(prefsKey()) || '{}');
    return { auto: p.auto === 'on' || p.auto === 'off' ? p.auto : null, allowCellular: p.allowCellular === true };
  } catch {
    return { auto: null, allowCellular: false };
  }
}

export function writeHuntMediaPrefs(change: Partial<HuntMediaPrefs>): HuntMediaPrefs {
  const next = { ...readHuntMediaPrefs(), ...change };
  safeLocalStorageSet(prefsKey(), JSON.stringify(next));
  emit();
  if (next.auto === 'on') void syncHuntMedia();
  return next;
}

// ── Jaktfönster ────────────────────────────────────────────────────────────

export interface HuntWindow {
  /** meetingId eller kalenderhändelsens id */
  key: string;
  markId: string;
  markName?: string;
  eventId: string | null;
  from: number;
  /** null = pågår */
  to: number | null;
  coordinatesJson?: string | null;
  mapLat?: number | null;
  mapLng?: number | null;
}

const WINDOWS_KEY = 'qvenari_hunt_media_windows_v1';
/** Bilder som dyker upp i Bilder i efterhand (glasögonklipp, wifi) hämtas så här länge efter jakten. */
export const KEEP_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;
/** En jakt som aldrig avslutades på telefonen räknas som slut efter så här lång tid. */
const MAX_HUNT_MS = 12 * 60 * 60 * 1000;

function readWindows(): HuntWindow[] {
  try { return JSON.parse(safeLocalStorageGet(WINDOWS_KEY) || '[]'); } catch { return []; }
}

function writeWindows(list: HuntWindow[]) {
  const now = Date.now();
  safeLocalStorageSet(WINDOWS_KEY, JSON.stringify(list.filter(w => windowEnd(w, now) + KEEP_WINDOW_MS > now)));
}

function windowEnd(w: HuntWindow, now: number): number {
  return w.to ?? Math.min(now, w.from + MAX_HUNT_MS);
}

/** Jakten har startat (eller öppnats) på telefonen. Samma key uppdateras; tidigaste start behålls. */
export function rememberHuntWindow(w: HuntWindow) {
  const list = readWindows();
  const old = list.find(x => x.key === w.key);
  const merged: HuntWindow = old
    ? { ...old, ...w, from: Math.min(old.from, w.from), to: w.to ?? old.to }
    : w;
  writeWindows([...list.filter(x => x.key !== w.key), merged]);
  if (readHuntMediaPrefs().auto === 'on') void syncHuntMedia();
}

export function finishHuntWindow(key: string, endedAt = Date.now()) {
  const list = readWindows();
  const w = list.find(x => x.key === key);
  if (!w || w.to != null) return;
  writeWindows(list.map(x => (x.key === key ? { ...x, to: endedAt } : x)));
  // Sista bilderna från jakten (marginalen efter slut) hämtas vid nästa körning.
  if (readHuntMediaPrefs().auto === 'on') void syncHuntMedia();
}

// ── Det som redan laddats upp ──────────────────────────────────────────────

interface Uploaded { url: string; markId: string; at: number; saved: boolean; skipped?: 'too_large' }
const UPLOADED_KEY = 'qvenari_hunt_media_uploaded_v1';
const UPLOADED_KEEP_MS = 30 * 24 * 60 * 60 * 1000;
const uploadedKey = (markId: string, sourceId: string) => `${markId}|${sourceId}`;

function readUploaded(): Record<string, Uploaded> {
  try { return JSON.parse(safeLocalStorageGet(UPLOADED_KEY) || '{}'); } catch { return {}; }
}

function writeUploaded(map: Record<string, Uploaded>) {
  const now = Date.now();
  const kept = Object.fromEntries(Object.entries(map).filter(([, v]) => now - v.at < UPLOADED_KEEP_MS));
  safeLocalStorageSet(UPLOADED_KEY, JSON.stringify(kept));
}

/** Manuellt tillagda (HuntMediaSection) minns också, så att den automatiska hämtningen hoppar över dem. */
export function markHuntMediaSaved(markId: string, items: { sourceId?: string | null; url: string }[]) {
  const map = readUploaded();
  for (const i of items) if (i.sourceId) map[uploadedKey(markId, i.sourceId)] = { url: i.url, markId, at: Date.now(), saved: true };
  writeUploaded(map);
}

// ── Status för UI:t ────────────────────────────────────────────────────────

export interface QueuedItem { item: MatchedItem; markId: string; markName?: string }
export interface HuntMediaSyncStatus {
  state: 'idle' | 'scanning' | 'uploading' | 'waitingForWifi' | 'noAccess' | 'unsupported';
  /** Det som laddas upp nu eller väntar (tydligt för jägaren vad som går iväg). */
  queue: QueuedItem[];
  currentId: string | null;
  done: number;
  /** Senast uppladdade (för "Senast hämtat …"). */
  last: { count: number; markName?: string; at: number } | null;
  error: string | null;
}

let status: HuntMediaSyncStatus = { state: 'idle', queue: [], currentId: null, done: 0, last: null, error: null };
const listeners = new Set<() => void>();
function emit() { listeners.forEach(l => l()); }
function setStatus(change: Partial<HuntMediaSyncStatus>) { status = { ...status, ...change }; emit(); }

export function getHuntMediaSyncStatus(): HuntMediaSyncStatus { return status; }
export function subscribeHuntMediaSync(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// ── Stöd i appen ───────────────────────────────────────────────────────────

let supported: Promise<boolean> | null = null;
/** Automatisk hämtning kräver ett appbygge med A3-metoderna (getAccess m.fl.). */
export function isAutoFetchSupported(): Promise<boolean> {
  if (!isHuntMediaAvailable()) return Promise.resolve(false);
  supported ??= HuntMediaNative.getAccess().then(() => true, () => false);
  return supported;
}

// ── Själva hämtningen ──────────────────────────────────────────────────────

/** Gräns för automatiskt uppladdade filer (efter komprimering). Större klipp väljs för hand. */
export const AUTO_MAX_BYTES = 100 * 1024 * 1024;
const MIN_INTERVAL_MS = 60 * 1000;

let running: Promise<void> | null = null;
let lastRunAt = 0;

/**
 * Hämtar det som ska laddas upp automatiskt. force = jägaren tryckte "Ladda upp nu" (även på
 * mobildata). Körs aldrig parallellt, och utan force högst en gång i minuten.
 */
export function syncHuntMedia(opts: { force?: boolean } = {}): Promise<void> {
  if (running) return running;
  if (!opts.force && Date.now() - lastRunAt < MIN_INTERVAL_MS) return Promise.resolve();
  lastRunAt = Date.now();
  running = runSync(!!opts.force).finally(() => { running = null; });
  return running;
}

/** Bara för tester. */
export function resetHuntMediaSyncForTests() {
  running = null; lastRunAt = 0; supported = null;
  status = { state: 'idle', queue: [], currentId: null, done: 0, last: null, error: null };
}

async function runSync(force: boolean): Promise<void> {
  const prefs = readHuntMediaPrefs();
  if (prefs.auto !== 'on' || !auth.isLoggedIn()) return;
  if (!(await isAutoFetchSupported())) { setStatus({ state: 'unsupported' }); return; }

  // Frågar aldrig om åtkomst här — det görs bara när jägaren själv trycker i appen.
  const access: LibraryAccess = (await HuntMediaNative.getAccess()).status;
  if (access !== 'authorized' && access !== 'limited') { setStatus({ state: 'noAccess', queue: [] }); return; }

  const now = Date.now();
  const windows = readWindows().filter(w => windowEnd(w, now) + KEEP_WINDOW_MS > now);
  if (windows.length === 0) { setStatus({ state: 'idle', queue: [] }); return; }

  setStatus({ state: 'scanning', error: null });
  const track = readGpsTrackPoints();
  const uploaded = readUploaded();
  const queue: QueuedItem[] = [];
  for (const w of windows) {
    const from = w.from - HUNT_MEDIA_MARGIN_MS;
    const to = Math.min(now, windowEnd(w, now) + HUNT_MEDIA_MARGIN_MS);
    const area = parseMarkArea(w.coordinatesJson, { lat: w.mapLat, lng: w.mapLng });
    let items: MatchedItem[];
    try {
      items = classifyLibraryItems(await findLibraryMedia(from, to), area, track);
    } catch {
      continue;
    }
    let onServer = new Set<string>();
    try {
      onServer = new Set((await api.huntMedia(w.markId, from, to)).map(m => m.sourceId).filter((x): x is string => !!x));
    } catch { /* API:ts unika index stoppar ändå dubbletter i jakten */ }
    for (const item of items) {
      if (!isAutoEligible(item, area) || onServer.has(item.id)) continue;
      const u = uploaded[uploadedKey(w.markId, item.id)];
      if (u?.saved || u?.skipped) continue;
      queue.push({ item, markId: w.markId, markName: w.markName });
    }
  }
  if (queue.length === 0) { setStatus({ state: 'idle', queue: [] }); return; }

  const allowCellular = force || prefs.allowCellular;
  if (!allowCellular) {
    const net = await HuntMediaNative.networkStatus().catch(() => null);
    if (!net?.wifi || net.constrained) { setStatus({ state: 'waitingForWifi', queue }); return; }
  }

  setStatus({ state: 'uploading', queue, done: 0, currentId: null });
  const byWindow = new Map<string, { w: HuntWindow; inputs: HuntMediaInput[] }>();
  const winFor = (q: QueuedItem) => windows.find(w => w.markId === q.markId && q.item.takenAt >= w.from - HUNT_MEDIA_MARGIN_MS && q.item.takenAt <= windowEnd(w, now) + HUNT_MEDIA_MARGIN_MS) || windows.find(w => w.markId === q.markId)!;
  let stoppedForWifi = false;
  for (const q of queue) {
    const key = uploadedKey(q.markId, q.item.id);
    setStatus({ currentId: q.item.id });
    let url = uploaded[key]?.url;
    if (!url) {
      try {
        url = await uploadLibraryItem(q.item.id, { compress: true, maxBytes: AUTO_MAX_BYTES, allowCellular });
      } catch (e: any) {
        if (e?.code === 'NO_WIFI') { stoppedForWifi = true; break; }
        if (e?.code === 'FILE_TOO_LARGE') {
          uploaded[key] = { url: '', markId: q.markId, at: Date.now(), saved: false, skipped: 'too_large' };
          writeUploaded(uploaded);
        }
        setStatus({ done: status.done + 1 });
        continue;
      }
      // Minns direkt: misslyckas sparandet nedan behöver filen inte laddas upp igen.
      uploaded[key] = { url, markId: q.markId, at: Date.now(), saved: false };
      writeUploaded(uploaded);
    }
    const w = winFor(q);
    const entry = byWindow.get(w.key) || { w, inputs: [] };
    entry.inputs.push(toHuntMediaInput(q.item, url, track));
    byWindow.set(w.key, entry);
    setStatus({ done: status.done + 1 });
  }

  // En sparning per jakt, så att jaktlaget får en notis och inte en per bild.
  let savedCount = 0;
  let lastMark: string | undefined;
  for (const { w, inputs } of byWindow.values()) {
    try {
      await api.addHuntMedia(w.markId, w.eventId, inputs);
      for (const i of inputs) if (i.sourceId) uploaded[uploadedKey(w.markId, i.sourceId)] = { ...uploaded[uploadedKey(w.markId, i.sourceId)], saved: true };
      writeUploaded(uploaded);
      savedCount += inputs.length;
      lastMark = w.markName;
    } catch (e: any) {
      setStatus({ error: e?.message || 'Kunde inte spara bilderna till jakten' });
    }
  }

  const remaining = stoppedForWifi ? queue.filter(q => !readUploaded()[uploadedKey(q.markId, q.item.id)]?.saved) : [];
  setStatus({
    state: stoppedForWifi ? 'waitingForWifi' : 'idle',
    queue: remaining,
    currentId: null,
    last: savedCount > 0 ? { count: savedCount, markName: lastMark, at: Date.now() } : status.last,
  });
}

/**
 * Kör hämtningen när appen öppnas eller kommer tillbaka, och var femte minut medan den är öppen.
 * Gör ingenting om inställningen är av.
 */
export function startHuntMediaAutoSync(): () => void {
  if (!isHuntMediaAvailable()) return () => {};
  const run = () => { if (readHuntMediaPrefs().auto === 'on') void syncHuntMedia(); };
  const onVisible = () => { if (document.visibilityState === 'visible') run(); };
  document.addEventListener('visibilitychange', onVisible);
  const tid = window.setInterval(run, 5 * 60 * 1000);
  run();
  return () => { document.removeEventListener('visibilitychange', onVisible); clearInterval(tid); };
}
